import React from 'react';
import { type PaidRoomEntry } from '../types';
import './PaidRoomsPage.css';

const GAME_TYPE_LABELS: Record<string, string> = {
  trivia: 'Trivia',
  'speed-challenge': 'Speed Challenge',
  'collaborative-puzzle': 'Collaborative Puzzle',
  'story-chain': 'Story Chain',
  'word-puzzle': 'Word Puzzle',
  'close-or-not': 'Close or Not',
  'picture-perfect': 'Picture Perfect',
  'drinking-game': 'Drinking Game',
  'odd-one-out': 'Odd One Out',
  'estimate-it': 'Estimate It',
  'true-or-silly': 'True or Silly?',
};

function getDisplayAvatar(avatar?: string): string {
  if (!avatar) return '👤';
  if (avatar.startsWith('/') || avatar.startsWith('http') || avatar.includes('.svg')) {
    return '👤';
  }
  return avatar;
}

interface PaidRoomsPageProps {
  paidRooms: PaidRoomEntry[];
  onRoomClick: (entry: PaidRoomEntry) => void;
}

const PaidRoomsPage: React.FC<PaidRoomsPageProps> = ({ paidRooms, onRoomClick }) => {
  const paidOnlyRooms = paidRooms.filter(
    (entry) => !entry.purchase.sessionId?.startsWith('promo-')
  );

  const formatTimeLeft = (expiresAt: number): string => {
    const ms = Math.max(0, expiresAt - Date.now());
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m left`;
    return 'Expired';
  };

  return (
    <div className="paid-rooms-page">
      <div className="paid-rooms-header">
        <h2 className="paid-rooms-page-title">
          <span className="title-prefix">&gt;</span> Paid Rooms
        </h2>
        <p className="paid-rooms-subtitle">
          All rooms purchased via Stripe ({paidOnlyRooms.length} total)
        </p>
      </div>

      <section className="paid-rooms-list-section">
        {paidOnlyRooms.length === 0 ? (
          <div className="paid-rooms-empty">
            <div className="paid-rooms-empty-icon">💳</div>
            <p className="paid-rooms-empty-text">No paid rooms yet</p>
            <p className="paid-rooms-empty-subtext">Paid rooms will appear here after Stripe purchases</p>
          </div>
        ) : (
          <div className="paid-rooms-grid">
            {paidOnlyRooms.map((entry, index) => {
              const { purchase, room } = entry;
              const timeLeft = formatTimeLeft(purchase.expiresAt);
              return (
                <div
                  key={purchase.roomName}
                  className="room-card paid-room-card"
                  onClick={() => onRoomClick(entry)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRoomClick(entry);
                    }
                  }}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="room-card-header">
                    <span className="room-code">{purchase.roomName}</span>
                    <div className="room-card-header-right">
                      <span className="room-type-chip room-type-paid">Paid</span>
                      <span className="room-time-ago">{timeLeft}</span>
                    </div>
                  </div>
                  <div className="room-card-body">
                    <div className="game-type">{purchase.packageName}</div>
                    <div className="player-count">
                      <span className="player-label">{purchase.duration} access</span>
                      {room && (
                        <>
                          <span className="separator">•</span>
                          <span className="player-icon">👥</span>
                          <span className="player-number">{room.players.length}</span>
                          <span className="player-label">player{room.players.length !== 1 ? 's' : ''}</span>
                          {room.gameType && (
                            <>
                              <span className="separator">•</span>
                              <span className="player-label">
                                {GAME_TYPE_LABELS[room.gameType] || room.gameType}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="room-card-footer">
                    {room && room.players.length > 0 ? (
                      <div className="player-avatars">
                        {room.players.slice(0, 4).map((player, i) => (
                          <span
                            key={player.id}
                            className="avatar"
                            style={{ zIndex: 4 - i }}
                          >
                            {getDisplayAvatar(player.avatar)}
                          </span>
                        ))}
                        {room.players.length > 4 && (
                          <span className="avatar more">+{room.players.length - 4}</span>
                        )}
                      </div>
                    ) : (
                      <span className="player-label muted">Not active</span>
                    )}
                    <span className="view-details">View →</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default PaidRoomsPage;
